import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCaptureFields } from '@/components/vsleads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

interface TimerTabProps {
  form: UseFormReturn<FormCaptureFields>;
}

export function TimerTab({ form }: TimerTabProps) {
  useLanguage();
  const timer = form.watch('timer');

  const formatTimeInput = (value: string) => {
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length <= 2) {
      return cleaned.padStart(2, '0') + ':00';
    }
    const minutes = cleaned.slice(0, -2).padStart(2, '0');
    const seconds = cleaned.slice(-2).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const handleTimeChange = (value: string) => {
    const formatted = formatTimeInput(value);
    form.setValue('timer.showAt', formatted, { shouldDirty: true });
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Ativar Captura por Tempo")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Exibir o formulário em um momento específico do vídeo")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="timer-enabled">{translateInterfaceText("Ativar temporizador")}</Label>
            <Switch
              id="timer-enabled"
              checked={timer.enabled}
              onCheckedChange={(checked) =>
                form.setValue('timer.enabled', checked, { shouldDirty: true })
              }
            />
          </div>
        </CardContent>
      </Card>

      {timer.enabled && (
        <>
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-base">{translateInterfaceText("Momento de Exibição")}</CardTitle>
              <CardDescription>
                {translateInterfaceText("Quando o formulário deve aparecer (formato: mm:ss)")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="show-at">{translateInterfaceText("Exibir em (minutos:segundos)")}</Label>
                <Input
                  id="show-at"
                  type="text"
                  value={timer.showAt}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  placeholder="00:00"
                  maxLength={5}
                  className="font-mono text-lg"
                />
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Digite apenas números. Ex: 130 = 01:30")}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-base">{translateInterfaceText("Comportamento do Vídeo")}</CardTitle>
              <CardDescription>
                {translateInterfaceText("Como o vídeo deve se comportar quando o formulário aparece")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="pause-on-open">{translateInterfaceText("Pausar vídeo ao abrir")}</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translateInterfaceText("O vídeo pausa quando o formulário aparecer")}</p>
                </div>
                <Switch
                  id="pause-on-open"
                  checked={timer.pauseOnOpen}
                  onCheckedChange={(checked) =>
                    form.setValue('timer.pauseOnOpen', checked, { shouldDirty: true })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="pause-on-submit">{translateInterfaceText("Pausar vídeo ao enviar")}</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translateInterfaceText("O vídeo pausa após o envio do formulário")}</p>
                </div>
                <Switch
                  id="pause-on-submit"
                  checked={timer.pauseOnSubmit}
                  onCheckedChange={(checked) =>
                    form.setValue('timer.pauseOnSubmit', checked, { shouldDirty: true })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="block-close">{translateInterfaceText("Bloquear fechar até enviar")}</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translateInterfaceText("Usuário não pode fechar sem preencher")}</p>
                </div>
                <Switch
                  id="block-close"
                  checked={timer.blockCloseUntilSubmit}
                  onCheckedChange={(checked) =>
                    form.setValue('timer.blockCloseUntilSubmit', checked, { shouldDirty: true })
                  }
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
